import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '../../../lib/api.js'
import { dateTime, money } from '../../../lib/format.js'
import { Card, Empty, Field, Notice, PageHead, RowsSkeleton, Segmented, StepList } from '../../../components/ui.js'
import { ConfirmDialog, toast } from '../../../components/feedback.js'
import { CheckCircleIcon, PlusIcon, SearchIcon, XIcon } from '../../../components/icons.js'
import { useOverview } from './Dashboard.js'
import {
  FILTER_FIELDS,
  OPS,
  PRIORITY_LABEL,
  ROUTING_LABEL,
  TRIGGERS,
  defaultRule,
  errorMessage,
  formFromRule,
  ruleSummary,
  type Condition,
  type Issue,
  type Priority,
  type Routing,
  type Rule,
  type RuleForm,
  type ScheduleType,
  type Template,
  type TriggerType,
} from './shared.js'

const STEPS = [
  { title: 'Basic information', description: 'Name, priority, status' },
  { title: 'Audience & filters', description: 'Which merchants' },
  { title: 'Wallet trigger', description: 'The balance condition' },
  { title: 'Notification channels', description: 'WhatsApp, email, fallback' },
  { title: 'Templates & variables', description: 'What the message says' },
  { title: 'Schedule & frequency', description: 'When, and how often' },
  { title: 'Review & activate', description: 'Test, then go live' },
]

const CATEGORIES: Array<[string, string]> = [
  ['low_balance', 'Low balance'],
  ['critical_balance', 'Critical balance'],
  ['depleted', 'Wallet depleted'],
  ['recovery', 'Recovery'],
  ['reminder', 'Recharge reminder'],
  ['usage', 'High usage'],
  ['summary', 'Balance summary'],
]

const NAME_IDEAS = ['Critical Wallet Balance Alert', 'Low Balance Warning', 'Wallet Recharge Reminder', 'Wallet Depleted', 'High Usage Notification']

function useDebounced<T>(value: T, ms = 400): T {
  const [current, setCurrent] = useState(value)
  useEffect(() => {
    const timer = window.setTimeout(() => setCurrent(value), ms)
    return () => window.clearTimeout(timer)
  }, [value, ms])
  return current
}

interface TemplatesResponse {
  items: Template[]
  variables: Array<{ key: string; label: string }>
}

export function useTemplates() {
  return useQuery({
    queryKey: ['admin', 'wallet-alerts', 'templates'],
    queryFn: () => api<TemplatesResponse>('/v1/admin/wallet-alerts/templates'),
  })
}

/** Create (no id) or edit (id) a rule. */
export function AdminWalletAlertWizardPage(): React.ReactNode {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const overview = useOverview()
  const [step, setStep] = useState(() => Math.min(6, Math.max(0, Number(params.get('step') ?? 0) || 0)))
  const [form, setForm] = useState<RuleForm | null>(id ? null : defaultRule())
  const [confirmActivate, setConfirmActivate] = useState(false)

  const existing = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'rule', id],
    queryFn: () => api<{ rule: Rule }>(`/v1/admin/wallet-alerts/rules/${id}`),
    enabled: Boolean(id),
  })

  useEffect(() => {
    if (existing.data && form === null) setForm(formFromRule(existing.data.rule))
  }, [existing.data, form])

  // New rules start in the platform's configured timezone.
  useEffect(() => {
    if (!id && overview.data && form && form.schedule.timezone === 'Asia/Kolkata' && overview.data.timezone !== 'Asia/Kolkata') {
      setForm({ ...form, schedule: { ...form.schedule, timezone: overview.data.timezone } })
    }
  }, [overview.data])

  const save = useMutation({
    mutationFn: (status: 'draft' | 'active') =>
      api<Rule>(id ? `/v1/admin/wallet-alerts/rules/${id}` : '/v1/admin/wallet-alerts/rules', {
        method: id ? 'PUT' : 'POST',
        body: { ...form, status },
      }),
    onSuccess: (rule, status) => {
      toast('ok', status === 'active' ? 'Rule activated' : 'Saved as draft', rule.name)
      navigate(`/admin/wallet-alerts/${rule.id}`)
    },
    onError: (error) => {
      setConfirmActivate(false)
      toast('error', 'Could not save the rule', errorMessage(error))
    },
  })

  const crumbs = [{ label: 'Wallet Alerts', to: '/admin/wallet-alerts' }, { label: id ? 'Edit rule' : 'New rule' }]

  if (id && existing.error) {
    return (
      <>
        <PageHead title="Edit alert rule" crumbs={crumbs} />
        <Card>
          <Empty error title="Could not load this rule">
            {errorMessage(existing.error)}
          </Empty>
        </Card>
      </>
    )
  }

  if (!form) {
    return (
      <>
        <PageHead title="Edit alert rule" crumbs={crumbs} />
        <Card>
          <RowsSkeleton rows={8} />
        </Card>
      </>
    )
  }

  const update = (patch: Partial<RuleForm>): void => setForm({ ...form, ...patch })
  const nameMissing = form.name.trim().length < 2

  return (
    <>
      <PageHead
        title={id ? `Edit: ${form.name || 'Alert rule'}` : 'Create alert rule'}
        subtitle="Move between steps freely — nothing is lost until you leave this page."
        crumbs={crumbs}
        actions={
          <button type="button" className="btn" disabled={nameMissing || save.isPending} onClick={() => save.mutate('draft')}>
            Save as draft
          </button>
        }
      />

      <div className="wa-wizard">
        <aside className="wa-wizard__aside">
          <Card flush>
            <div style={{ padding: 8 }}>
              <StepList steps={STEPS} current={step} onSelect={setStep} />
            </div>
          </Card>
        </aside>

        <div className="grid">
          {step === 0 && <BasicStep form={form} update={update} />}
          {step === 1 && <AudienceStep form={form} update={update} />}
          {step === 2 && <TriggerStep form={form} update={update} ruleId={id} />}
          {step === 3 && <ChannelStep form={form} update={update} />}
          {step === 4 && <TemplateStep form={form} />}
          {step === 5 && <ScheduleStep form={form} update={update} />}
          {step === 6 && <ReviewStep form={form} onActivate={() => setConfirmActivate(true)} onDraft={() => save.mutate('draft')} saving={save.isPending} />}

          <div className="wa-wizard__foot">
            <button type="button" className="btn" disabled={step === 0} onClick={() => setStep(step - 1)}>
              Back
            </button>
            {step < 6 && (
              <button type="button" className="btn btn--primary" disabled={step === 0 && nameMissing} onClick={() => setStep(step + 1)}>
                Continue — {STEPS[step + 1]?.title}
              </button>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmActivate}
        title="Activate this rule?"
        message={
          <>
            <p style={{ marginBottom: 8 }}>{ruleSummary(form)}</p>
            <p>Real messages will go to real merchants from the next scheduled run.</p>
          </>
        }
        confirmLabel="Activate rule"
        pending={save.isPending}
        onConfirm={() => save.mutate('active')}
        onClose={() => setConfirmActivate(false)}
      />
    </>
  )
}

type StepProps = { form: RuleForm; update: (patch: Partial<RuleForm>) => void }

// ── Step 1 ──────────────────────────────────────────────────────────────────

function BasicStep({ form, update }: StepProps): React.ReactNode {
  return (
    <Card title="Basic information" subtitle="Each rule is independent — create one per severity level.">
      <div className="wa-form">
        <Field label="Alert rule name">
          <input className="input" value={form.name} maxLength={120} placeholder="Low Balance Warning" onChange={(e) => update({ name: e.target.value })} />
        </Field>
        <div className="wa-chips">
          {NAME_IDEAS.map((idea) => (
            <button key={idea} type="button" className="btn btn--sm" onClick={() => update({ name: idea })}>
              {idea}
            </button>
          ))}
        </div>
        <Field label="Internal description" hint="Only operators see this.">
          <textarea className="textarea" rows={3} maxLength={500} value={form.description} onChange={(e) => update({ description: e.target.value })} />
        </Field>
        <div className="wa-row">
          <Field label="Rule category">
            <select className="select" value={form.category} onChange={(e) => update({ category: e.target.value })}>
              {CATEGORIES.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Status after saving">
            <Segmented
              label="Status"
              value={form.status}
              onChange={(status) => update({ status })}
              options={[
                { key: 'draft', label: 'Draft' },
                { key: 'active', label: 'Active' },
              ]}
            />
          </Field>
        </div>
        <Field label="Priority">
          <Segmented
            label="Priority"
            value={form.priority}
            onChange={(priority: Priority) => update({ priority })}
            options={(Object.keys(PRIORITY_LABEL) as Priority[]).map((key) => ({ key, label: PRIORITY_LABEL[key] }))}
          />
        </Field>
        <span className="field__hint">Activation always goes through the review step, where the rule is validated and can be tested first.</span>
      </div>
    </Card>
  )
}

// ── Step 2 ──────────────────────────────────────────────────────────────────

interface PreviewResult {
  total: number
  matching: number
  excluded: number
  triggered: number
  withEmail: number
  withWhatsapp: number
  missing: number
  sample: Array<{ id: string; name: string; email: string | null; phone: string | null; balance: string; currency: string; status: string; emailVerified: boolean }>
}

function AudienceStep({ form, update }: StepProps): React.ReactNode {
  const audience = form.audience
  const setAudience = (patch: Partial<RuleForm['audience']>): void => update({ audience: { ...audience, ...patch } })

  return (
    <>
      <Card title="Audience" subtitle="Who this rule may alert. Contact details always come from the merchant's own account.">
        <div className="wa-form">
          <div className="wa-choice">
            {(
              [
                ['all', 'All merchants', 'Every eligible merchant account.'],
                ['specific', 'Specific merchants', 'Search and pick individual accounts.'],
                ['filters', 'Segments & dynamic filters', 'Combine conditions with AND / OR groups.'],
              ] as const
            ).map(([key, title, hint]) => (
              <button key={key} type="button" className={audience.mode === key ? 'wa-choice__item is-active' : 'wa-choice__item'} onClick={() => setAudience({ mode: key })}>
                <strong>{title}</strong>
                <small>{hint}</small>
              </button>
            ))}
          </div>

          <label className="checkbox-row">
            <input type="checkbox" checked={audience.includeInactive} onChange={(e) => setAudience({ includeInactive: e.target.checked })} />
            <span>Include paused / inactive merchant accounts</span>
          </label>

          {audience.mode === 'specific' && <MerchantPicker selected={audience.merchantIds} onChange={(merchantIds) => setAudience({ merchantIds })} />}
          {audience.mode === 'filters' && <FilterBuilder audience={audience} onChange={setAudience} />}
          {audience.mode === 'filters' && (
            <span className="field__hint">
              Segments: merchants are segmented by plan (Starter, Professional, Scale). Tags and notification-consent flags do not exist for
              merchant accounts yet, so they are not offered as filters.
            </span>
          )}
        </div>
      </Card>

      <AudiencePreview form={form} />
    </>
  )
}

function MerchantPicker({ selected, onChange }: { selected: string[]; onChange: (ids: string[]) => void }): React.ReactNode {
  const [q, setQ] = useState('')
  const search = useDebounced(q, 300)

  const results = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'merchants', search],
    queryFn: () => api<{ items: PreviewResult['sample'] }>('/v1/admin/wallet-alerts/merchants', { query: { q: search } }),
    enabled: search.trim().length > 0,
  })

  const chosen = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'merchants', 'ids', selected.join(',')],
    queryFn: () => api<{ items: PreviewResult['sample'] }>('/v1/admin/wallet-alerts/merchants', { query: { ids: selected.join(',') } }),
    enabled: selected.length > 0,
  })

  const names = new Map((chosen.data?.items ?? []).map((m) => [m.id, m.name]))
  const items = results.data?.items ?? []
  const unselected = items.filter((m) => !selected.includes(m.id))

  return (
    <div className="wa-form">
      <div className="input-group">
        <SearchIcon size={16} />
        <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, phone or merchant ID" />
      </div>

      {search && (
        <div className="table-wrap" style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
          {results.isPending ? (
            <RowsSkeleton rows={3} />
          ) : items.length === 0 ? (
            <Empty title="No merchants match" />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Merchant</th>
                  <th>Email</th>
                  <th>WhatsApp</th>
                  <th className="right">Balance</th>
                  <th className="right">
                    {unselected.length > 0 && (
                      <button type="button" className="btn btn--sm" onClick={() => onChange([...selected, ...unselected.map((m) => m.id)])}>
                        Select all {unselected.length}
                      </button>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id}>
                    <td>
                      {m.name}
                      <span className="table__sub">#{m.id}</span>
                    </td>
                    <td>{m.email ?? <span className="faint">none</span>}</td>
                    <td>{m.phone ?? <span className="faint">none</span>}</td>
                    <td className="right num">{money(m.balance, m.currency)}</td>
                    <td className="right">
                      {selected.includes(m.id) ? (
                        <button type="button" className="btn btn--sm" onClick={() => onChange(selected.filter((id) => id !== m.id))}>
                          Remove
                        </button>
                      ) : (
                        <button type="button" className="btn btn--sm btn--primary" onClick={() => onChange([...selected, m.id])}>
                          Add
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong style={{ fontSize: '.85rem' }}>{selected.length} merchant{selected.length === 1 ? '' : 's'} selected</strong>
        {selected.length > 0 && (
          <button type="button" className="btn btn--sm" onClick={() => onChange([])}>
            Clear all
          </button>
        )}
      </div>
      {selected.length > 0 && (
        <div className="wa-chips">
          {selected.map((merchantId) => (
            <span key={merchantId} className="wa-chip">
              {names.get(merchantId) ?? `#${merchantId}`}
              <button type="button" aria-label="Remove" onClick={() => onChange(selected.filter((x) => x !== merchantId))}>
                <XIcon size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function FilterBuilder({ audience, onChange }: { audience: RuleForm['audience']; onChange: (patch: Partial<RuleForm['audience']>) => void }): React.ReactNode {
  const groups = audience.groups

  const setGroup = (index: number, patch: Partial<RuleForm['audience']['groups'][number]>): void =>
    onChange({ groups: groups.map((group, i) => (i === index ? { ...group, ...patch } : group)) })

  const setCondition = (gi: number, ci: number, patch: Partial<Condition>): void =>
    setGroup(gi, { conditions: groups[gi]!.conditions.map((c, i) => (i === ci ? { ...c, ...patch } : c)) })

  const fresh = (): Condition => ({ field: 'balance', op: 'lt', value: 500 })

  return (
    <div className="wa-form">
      {groups.length > 1 && (
        <Field label="Groups combine with">
          <Segmented
            label="Group combinator"
            value={audience.combinator}
            onChange={(combinator) => onChange({ combinator })}
            options={[
              { key: 'and', label: 'AND — all groups' },
              { key: 'or', label: 'OR — any group' },
            ]}
          />
        </Field>
      )}

      {groups.map((group, gi) => (
        <div key={gi} className="wa-form">
          {gi > 0 && <span className="wa-join">{audience.combinator}</span>}
          <div className="wa-group">
            <div className="wa-group__head">
              <span>
                Group {gi + 1} — match{' '}
                <select className="select" style={{ display: 'inline-block', width: 'auto', minWidth: 0, height: 28, padding: '0 24px 0 8px' }} value={group.combinator} onChange={(e) => setGroup(gi, { combinator: e.target.value as 'and' | 'or' })}>
                  <option value="and">all</option>
                  <option value="or">any</option>
                </select>{' '}
                of these
              </span>
              <button type="button" className="btn btn--sm" onClick={() => onChange({ groups: groups.filter((_, i) => i !== gi) })}>
                Remove group
              </button>
            </div>

            {group.conditions.map((condition, ci) => {
              const field = FILTER_FIELDS.find((f) => f.key === condition.field) ?? FILTER_FIELDS[0]!
              const ops = OPS[field.kind] ?? []
              const noValue = field.kind === 'bool' || condition.op === 'is_empty'

              return (
                <div key={ci} className="wa-cond">
                  <select
                    className="select"
                    aria-label="Field"
                    value={condition.field}
                    onChange={(e) => {
                      const next = FILTER_FIELDS.find((f) => f.key === e.target.value)!
                      setCondition(gi, ci, { field: next.key, op: OPS[next.kind]![0]!.key, value: next.kind === 'enum' ? next.options![0]! : next.kind === 'bool' ? null : '' })
                    }}
                  >
                    {FILTER_FIELDS.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                  <select className="select" aria-label="Operator" value={condition.op} onChange={(e) => setCondition(gi, ci, { op: e.target.value })}>
                    {ops.map((op) => (
                      <option key={op.key} value={op.key}>
                        {op.label}
                      </option>
                    ))}
                  </select>
                  {noValue ? (
                    <span />
                  ) : field.kind === 'enum' ? (
                    <select className="select" aria-label="Value" value={String(condition.value ?? '')} onChange={(e) => setCondition(gi, ci, { value: e.target.value })}>
                      {field.options!.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="input"
                      aria-label="Value"
                      type={field.kind === 'text' ? 'text' : 'number'}
                      value={condition.value === null ? '' : String(condition.value)}
                      onChange={(e) => setCondition(gi, ci, { value: field.kind === 'text' ? e.target.value : e.target.value === '' ? '' : Number(e.target.value) })}
                    />
                  )}
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label="Remove condition"
                    onClick={() => setGroup(gi, { conditions: group.conditions.filter((_, i) => i !== ci) })}
                  >
                    <XIcon size={16} />
                  </button>
                </div>
              )
            })}

            <div>
              <button type="button" className="btn btn--sm" onClick={() => setGroup(gi, { conditions: [...group.conditions, fresh()] })}>
                <PlusIcon size={14} /> Add condition
              </button>
            </div>
          </div>
        </div>
      ))}

      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn--sm" onClick={() => onChange({ groups: [...groups, { combinator: 'and', conditions: [fresh()] }] })}>
          <PlusIcon size={14} /> Add group
        </button>
        {groups.length === 0 && (
          <button
            type="button"
            className="btn btn--sm"
            onClick={() =>
              onChange({
                combinator: 'and',
                groups: [
                  { combinator: 'and', conditions: [{ field: 'balance', op: 'lt', value: 500 }, { field: 'status', op: 'eq', value: 'active' }] },
                  { combinator: 'or', conditions: [{ field: 'has_whatsapp', op: 'is_true', value: null }, { field: 'has_email', op: 'is_true', value: null }] },
                ],
              })
            }
          >
            Start from example: balance &lt; ₹500 AND active AND (WhatsApp OR email)
          </button>
        )}
      </div>
    </div>
  )
}

function AudiencePreview({ form }: { form: RuleForm }): React.ReactNode {
  const payload = useDebounced(
    JSON.stringify({
      audience: form.audience,
      rule: {
        triggerType: form.triggerType,
        thresholdAmount: form.thresholdAmount,
        thresholdMax: form.thresholdMax,
        currency: form.currency,
        triggerConfig: form.triggerConfig,
      },
    }),
    500,
  )

  const preview = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'preview', payload],
    queryFn: () => api<PreviewResult>('/v1/admin/wallet-alerts/preview-audience', { method: 'POST', body: JSON.parse(payload) }),
    enabled: form.audience.mode !== 'specific' || form.audience.merchantIds.length > 0,
  })

  const data = preview.data

  return (
    <Card title="Live audience preview" subtitle="Evaluated on the server against current wallet balances.">
      {preview.error && <Notice kind="error" title="Preview failed">{errorMessage(preview.error)}</Notice>}
      {!data && !preview.error && <RowsSkeleton rows={2} />}
      {data && (
        <div className="wa-form">
          <div className="wa-preview-counts">
            <div className="wa-count">
              <strong>{data.matching.toLocaleString('en-IN')}</strong>
              <span>Matching merchants</span>
            </div>
            <div className="wa-count">
              <strong style={{ color: 'var(--brand)' }}>{data.triggered.toLocaleString('en-IN')}</strong>
              <span>Would trigger now</span>
            </div>
            <div className="wa-count">
              <strong>{data.withEmail.toLocaleString('en-IN')}</strong>
              <span>With email</span>
            </div>
            <div className="wa-count">
              <strong>{data.withWhatsapp.toLocaleString('en-IN')}</strong>
              <span>With WhatsApp</span>
            </div>
            <div className="wa-count">
              <strong style={{ color: data.missing > 0 ? 'var(--warn)' : undefined }}>{data.missing.toLocaleString('en-IN')}</strong>
              <span>Missing contact</span>
            </div>
            <div className="wa-count">
              <strong>{data.excluded.toLocaleString('en-IN')}</strong>
              <span>Excluded by filters</span>
            </div>
          </div>

          {data.sample.length > 0 ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Sample (would trigger)</th>
                    <th>Email</th>
                    <th>WhatsApp</th>
                    <th className="right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sample.map((m) => (
                    <tr key={m.id}>
                      <td>
                        {m.name}
                        <span className="table__sub">#{m.id}</span>
                      </td>
                      <td>
                        {m.email ?? <span className="faint">none</span>}
                        {m.email && m.emailVerified && <span className="table__sub">verified</span>}
                      </td>
                      <td>{m.phone ?? <span className="faint">none</span>}</td>
                      <td className="right num">{money(m.balance, m.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <span className="faint" style={{ fontSize: '.84rem' }}>
              No merchant in this audience meets the trigger right now.
            </span>
          )}
        </div>
      )}
    </Card>
  )
}

// ── Step 3 ──────────────────────────────────────────────────────────────────

function TriggerStep({ form, update, ruleId }: StepProps & { ruleId?: string }): React.ReactNode {
  const others = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'rules', 'thresholds'],
    queryFn: () =>
      api<{ thresholds: Array<{ id: string; name: string; amount: number; priority: string; triggerType: string; status: string }> }>(
        '/v1/admin/wallet-alerts/rules',
        { query: { perPage: 1 } },
      ),
  })

  const needsAmount = form.triggerType !== 'no_recharge_days'
  const config = form.triggerConfig

  return (
    <>
      <Card title="Wallet trigger" subtitle="The condition that makes a merchant due for this alert.">
        <div className="wa-form">
          <div className="wa-choice">
            {TRIGGERS.map((trigger) => (
              <button
                key={trigger.key}
                type="button"
                disabled={!trigger.available}
                className={['wa-choice__item', form.triggerType === trigger.key ? 'is-active' : '', trigger.available ? '' : 'is-disabled'].join(' ')}
                onClick={() =>
                  trigger.available &&
                  update({
                    triggerType: trigger.key as TriggerType,
                    thresholdAmount: trigger.key === 'depleted' ? 0 : form.thresholdAmount ?? 500,
                  })
                }
              >
                <strong>
                  {trigger.label} {!trigger.available && <small>(unavailable)</small>}
                </strong>
                <small>{trigger.description}</small>
              </button>
            ))}
          </div>

          <div className="wa-row">
            {needsAmount && (
              <Field label={form.triggerType === 'range' ? 'From amount' : form.triggerType === 'usage_over' ? 'Usage amount' : form.triggerType === 'depleted' ? 'Depleted at or below' : 'Threshold amount'}>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  value={form.thresholdAmount ?? ''}
                  onChange={(e) => update({ thresholdAmount: e.target.value === '' ? null : Number(e.target.value) })}
                />
              </Field>
            )}
            {form.triggerType === 'range' && (
              <Field label="To amount">
                <input className="input" type="number" step="0.01" value={form.thresholdMax ?? ''} onChange={(e) => update({ thresholdMax: e.target.value === '' ? null : Number(e.target.value) })} />
              </Field>
            )}
            {form.triggerType === 'usage_over' && (
              <Field label="Usage window (days)">
                <input className="input" type="number" min={1} max={365} value={config.usageWindowDays} onChange={(e) => update({ triggerConfig: { ...config, usageWindowDays: Number(e.target.value) || 1 } })} />
              </Field>
            )}
            {form.triggerType === 'no_recharge_days' && (
              <Field label="Days without a recharge">
                <input className="input" type="number" min={1} max={365} value={config.days} onChange={(e) => update({ triggerConfig: { ...config, days: Number(e.target.value) || 1 } })} />
              </Field>
            )}
            <Field label="Wallet currency" hint="Rules never compare amounts across currencies.">
              <select className="select" value={form.currency ?? ''} onChange={(e) => update({ currency: e.target.value || null })}>
                <option value="">Any (each wallet in its own currency)</option>
                <option value="INR">INR only</option>
                <option value="USD">USD only</option>
              </select>
            </Field>
          </div>

          {(form.triggerType === 'below' || form.triggerType === 'depleted' || form.triggerType === 'range' || form.triggerType === 'above') && (
            <Field
              label="Recovery threshold (optional)"
              hint={
                form.triggerType === 'above'
                  ? 'Re-arm only once the balance falls back to this amount.'
                  : 'Re-arm only once the balance climbs back to this amount, so a balance wobbling around the threshold is not alerted again and again.'
              }
            >
              <input
                className="input"
                type="number"
                step="0.01"
                placeholder="Re-arm as soon as the condition clears"
                value={config.recoveryAmount ?? ''}
                onChange={(e) => update({ triggerConfig: { ...config, recoveryAmount: e.target.value === '' ? null : Number(e.target.value) } })}
              />
            </Field>
          )}
        </div>
      </Card>

      {needsAmount && form.triggerType !== 'usage_over' && (
        <Card title="Balance scale" subtitle="Where this rule sits among your other thresholds.">
          <BalanceScale
            current={{ name: form.name || 'This rule', amount: form.thresholdAmount ?? 0, max: form.triggerType === 'range' ? form.thresholdMax : null }}
            others={(others.data?.thresholds ?? []).filter((t) => t.id !== ruleId && t.triggerType !== 'usage_over')}
            currency={form.currency ?? 'INR'}
          />
        </Card>
      )}
    </>
  )
}

function BalanceScale({
  current,
  others,
  currency,
}: {
  current: { name: string; amount: number; max: number | null }
  others: Array<{ id: string; name: string; amount: number; status: string }>
  currency: string
}): React.ReactNode {
  const amounts = [current.amount, current.max ?? current.amount, ...others.map((o) => o.amount)]
  const top = Math.max(1000, ...amounts) * 1.15
  const bottom = Math.min(0, ...amounts)
  const pos = (value: number): string => `${((value - bottom) / (top - bottom)) * 100}%`

  return (
    <div>
      <div className="wa-scale">
        <div className="wa-scale__bar" />
        {others.map((other) => (
          <div key={other.id} className="wa-scale__mark" style={{ left: pos(other.amount), opacity: other.status === 'active' ? 1 : 0.55 }} title={`${other.name} (${other.status})`}>
            <span>{money(other.amount, currency)}</span>
            <span className="wa-scale__pin" />
          </div>
        ))}
        <div className="wa-scale__mark is-current" style={{ left: pos(current.amount) }} title={current.name}>
          <span>{money(current.amount, currency)}</span>
          <span className="wa-scale__pin" />
        </div>
        {current.max !== null && (
          <div className="wa-scale__mark is-current" style={{ left: pos(current.max) }}>
            <span>{money(current.max, currency)}</span>
            <span className="wa-scale__pin" />
          </div>
        )}
        <div className="wa-scale__ticks">
          <span>{money(bottom, currency)}</span>
          <span>{money(top / 2, currency)}</span>
          <span>{money(top, currency)}</span>
        </div>
      </div>
      {others.length > 0 && (
        <div className="wa-chips" style={{ marginTop: 12 }}>
          {others.map((other) => (
            <span key={other.id} className="badge">
              {other.name}: {money(other.amount, currency)}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Step 4 ──────────────────────────────────────────────────────────────────

function ChannelStep({ form, update }: StepProps): React.ReactNode {
  const overview = useOverview()
  const templates = useTemplates()
  const routing = form.channels.routing
  const uses = (channel: 'email' | 'whatsapp'): boolean => routing === channel || routing === 'both' || routing.includes(channel)

  const options = (channel: 'email' | 'whatsapp'): Template[] => (templates.data?.items ?? []).filter((t) => t.channel === channel)
  const chosen = (channel: 'email' | 'whatsapp'): Template | undefined =>
    options(channel).find((t) => t.id === (channel === 'email' ? form.emailTemplateId : form.whatsappTemplateId))

  const wa = chosen('whatsapp')
  const em = chosen('email')

  return (
    <>
      <Card title="Channel routing" subtitle="Email and WhatsApp are delivered and tracked separately.">
        <div className="wa-choice">
          {(Object.keys(ROUTING_LABEL) as Routing[]).map((key) => (
            <button
              key={key}
              type="button"
              className={routing === key ? 'wa-choice__item is-active' : 'wa-choice__item'}
              onClick={() => update({ channels: { ...form.channels, routing: key } })}
            >
              <strong>{ROUTING_LABEL[key]}</strong>
              <small>
                {key === 'both'
                  ? 'Both channels, independently.'
                  : key.includes('_then_')
                    ? 'Fallback runs only if the first channel fails for a transient reason (provider down, wallet empty), or the merchant has no contact for it.'
                    : `Only ${ROUTING_LABEL[key]}.`}
              </small>
            </button>
          ))}
        </div>
      </Card>

      {uses('whatsapp') && (
        <Card title="WhatsApp" subtitle="Business-initiated WhatsApp messages must use a Meta-approved template.">
          <div className="wa-form">
            {overview.data && !overview.data.channels.whatsapp && (
              <Notice kind="warn" title="WhatsApp is not set up">
                Wakit is not configured. Add the base URL and API key in <Link to="/admin/integrations">Integrations → OTP</Link> before this channel can send.
              </Notice>
            )}
            <Field label="WhatsApp template">
              <select className="select" value={form.whatsappTemplateId ?? ''} onChange={(e) => update({ whatsappTemplateId: e.target.value || null })}>
                <option value="">Select a template…</option>
                {options('whatsapp').map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {t.approvalStatus === 'approved' ? 'approved' : t.approvalStatus} ({t.language})
                  </option>
                ))}
              </select>
            </Field>
            {wa && (
              <Notice kind={wa.approvalStatus === 'approved' && wa.status === 'active' ? 'ok' : 'warn'} title={`Template status: ${wa.approvalStatus}${wa.status === 'draft' ? ' · draft' : ''}`}>
                {wa.providerTemplateId ? `Linked to Wakit template “${wa.providerTemplateId}”. ` : 'Not linked to a Wakit template yet. '}
                {wa.approvalStatus !== 'approved' && 'It cannot be used by an active rule until it is approved. '}
                <Link to={`/admin/wallet-alerts/templates?edit=${wa.id}`}>Open template</Link>
              </Notice>
            )}
          </div>
        </Card>
      )}

      {uses('email') && (
        <Card title="Email" subtitle="Sent through the platform's SMTP sender.">
          <div className="wa-form">
            {overview.data && !overview.data.channels.email && (
              <Notice kind="warn" title="Email is not set up">
                Configure the SMTP sender in <Link to="/admin/integrations">Integrations → Email</Link> first.
              </Notice>
            )}
            <Field label="Email template">
              <select className="select" value={form.emailTemplateId ?? ''} onChange={(e) => update({ emailTemplateId: e.target.value || null })}>
                <option value="">Select a template…</option>
                {options('email').map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.status === 'draft' ? ' (draft)' : ''}
                  </option>
                ))}
              </select>
            </Field>
            <div className="wa-row">
              <Field label="Sender name" hint="Blank uses the platform sender name. The address is always the platform's.">
                <input className="input" value={form.channels.fromName} maxLength={120} onChange={(e) => update({ channels: { ...form.channels, fromName: e.target.value } })} />
              </Field>
              <Field label="Reply-to address" hint="Optional.">
                <input className="input" type="email" value={form.channels.replyTo} onChange={(e) => update({ channels: { ...form.channels, replyTo: e.target.value } })} />
              </Field>
            </div>
            {em && em.status === 'draft' && (
              <Notice kind="warn" title="This template is a draft">
                Activate it under <Link to={`/admin/wallet-alerts/templates?edit=${em.id}`}>Templates</Link> before activating the rule.
              </Notice>
            )}
          </div>
        </Card>
      )}
    </>
  )
}

// ── Step 5 ──────────────────────────────────────────────────────────────────

interface TemplatePreview {
  subject: string | null
  body: string
  textBody: string | null
  whatsappParams: string[]
  unknownVariables: string[]
  merchant: { id: string; name: string; sample: boolean }
}

export function TemplatePreviewPane({ template, merchantId, threshold }: { template: Template; merchantId?: string; threshold?: number | null }): React.ReactNode {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop')
  const payload = useDebounced(
    JSON.stringify({
      template: {
        name: template.name,
        channel: template.channel,
        language: template.language,
        subject: template.subject,
        body: template.body,
        textBody: template.textBody,
        variables: template.variables,
        providerTemplateId: template.providerTemplateId,
        status: template.status,
      },
      merchantId: merchantId || undefined,
      thresholdAmount: threshold ?? 500,
    }),
    350,
  )

  const preview = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'template-preview', payload],
    queryFn: () => api<TemplatePreview>('/v1/admin/wallet-alerts/templates/preview', { method: 'POST', body: JSON.parse(payload) }),
  })

  if (preview.error) return <Notice kind="error" title="Preview failed">{errorMessage(preview.error)}</Notice>
  if (!preview.data) return <RowsSkeleton rows={4} />
  const data = preview.data

  return (
    <div className="wa-form">
      {data.unknownVariables.length > 0 && (
        <Notice kind="error" title="Unknown variables">
          {data.unknownVariables.map((v) => `{{${v}}}`).join(', ')} cannot be filled and would be sent as-is.
        </Notice>
      )}
      <span className="faint" style={{ fontSize: '.8rem' }}>
        Rendered for {data.merchant.sample ? 'a sample merchant' : `${data.merchant.name} (#${data.merchant.id})`}.
      </span>

      {template.channel === 'whatsapp' ? (
        <>
          <div className="wa-bubble-wrap">
            <div className="wa-bubble">{data.body}</div>
          </div>
          <span className="field__hint">
            WhatsApp sends the approved template <code>{template.providerTemplateId || '—'}</code> with parameters:{' '}
            {data.whatsappParams.length > 0 ? data.whatsappParams.map((p, i) => `{{${i + 1}}} = ${p || '(empty)'}`).join(' · ') : 'none mapped'}.
            The text above is your preview copy; the approved template's own wording is what arrives.
          </span>
        </>
      ) : (
        <>
          <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: '.88rem' }}>Subject: {data.subject}</strong>
            <Segmented
              label="Preview device"
              value={device}
              onChange={setDevice}
              options={[
                { key: 'desktop', label: 'Desktop' },
                { key: 'mobile', label: 'Mobile' },
              ]}
            />
          </div>
          <iframe
            title="Email preview"
            className={device === 'mobile' ? 'wa-email-frame is-mobile' : 'wa-email-frame'}
            sandbox=""
            srcDoc={`<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width"><style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#1f2937;line-height:1.55;padding:20px;margin:0}</style></head><body>${data.body}</body></html>`}
          />
        </>
      )}
    </div>
  )
}

function TemplateStep({ form }: { form: RuleForm }): React.ReactNode {
  const templates = useTemplates()
  const routing = form.channels.routing
  const items = templates.data?.items ?? []
  const chosen = [
    routing !== 'email' ? items.find((t) => t.id === form.whatsappTemplateId) : undefined,
    routing !== 'whatsapp' ? items.find((t) => t.id === form.emailTemplateId) : undefined,
  ].filter((t): t is Template => Boolean(t))

  return (
    <>
      <Card
        title="Templates & variables"
        subtitle="Variables are filled from the merchant's own account at send time."
        action={
          <Link className="btn btn--sm" to="/admin/wallet-alerts/templates">
            Manage templates
          </Link>
        }
      >
        <div className="wa-vars">
          {(templates.data?.variables ?? []).map((v) => (
            <button key={v.key} type="button" title={v.label} onClick={() => void navigator.clipboard?.writeText(`{{${v.key}}}`).then(() => toast('info', `Copied {{${v.key}}}`))}>
              {`{{${v.key}}}`}
            </button>
          ))}
        </div>
        <span className="field__hint">Click a variable to copy it. Edit template content under Manage templates — every save keeps a version.</span>
      </Card>

      {chosen.length === 0 && (
        <Card>
          <Empty title="No template selected">Pick templates in the Notification channels step.</Empty>
        </Card>
      )}

      {chosen.map((template) => (
        <Card key={template.id} title={`${template.channel === 'email' ? 'Email' : 'WhatsApp'} — ${template.name}`} subtitle={`Version ${template.version} · ${template.status}`}>
          <TemplatePreviewPane template={template} threshold={form.thresholdAmount} />
        </Card>
      ))}
    </>
  )
}

// ── Step 6 ──────────────────────────────────────────────────────────────────

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fromLocalInput(value: string): string | null {
  return value ? new Date(value).toISOString() : null
}

function ScheduleStep({ form, update }: StepProps): React.ReactNode {
  const schedule = form.schedule
  const frequency = form.frequency
  const setSchedule = (patch: Partial<RuleForm['schedule']>): void => update({ schedule: { ...schedule, ...patch } })
  const setFrequency = (patch: Partial<RuleForm['frequency']>): void => update({ frequency: { ...frequency, ...patch } })

  const types: Array<[ScheduleType, string, string]> = [
    ['immediate', 'Immediate', 'Checked every 5 minutes; alerts as soon as the condition is met.'],
    ['hourly', 'Hourly', 'Every hour at a set minute.'],
    ['daily', 'Daily', 'Once a day at a set time.'],
    ['weekly', 'Weekly', 'On selected weekdays.'],
    ['monthly', 'Monthly', 'On a day of the month.'],
    ['once', 'One-time', 'A single evaluation, then pauses.'],
  ]

  return (
    <>
      <Card title="Schedule" subtitle="Balances are re-read at every run — never the balance from when the rule was created.">
        <div className="wa-form">
          <div className="wa-choice">
            {types.map(([key, label, hint]) => (
              <button key={key} type="button" className={schedule.type === key ? 'wa-choice__item is-active' : 'wa-choice__item'} onClick={() => setSchedule({ type: key })}>
                <strong>{label}</strong>
                <small>{hint}</small>
              </button>
            ))}
          </div>

          <div className="wa-row">
            {schedule.type !== 'immediate' && schedule.type !== 'once' && (
              <Field label={schedule.type === 'hourly' ? 'Minute past the hour' : 'Time'}>
                <input className="input" type="time" value={schedule.time} onChange={(e) => setSchedule({ time: e.target.value || '10:00' })} />
              </Field>
            )}
            {schedule.type === 'monthly' && (
              <Field label="Day of month" hint="1–28, so it exists every month.">
                <input className="input" type="number" min={1} max={28} value={schedule.dayOfMonth} onChange={(e) => setSchedule({ dayOfMonth: Math.min(28, Math.max(1, Number(e.target.value) || 1)) })} />
              </Field>
            )}
            <Field label="Timezone">
              <input className="input" value={schedule.timezone} onChange={(e) => setSchedule({ timezone: e.target.value })} />
            </Field>
          </div>

          {schedule.type === 'weekly' && (
            <Field label="Weekdays">
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {WEEKDAYS.map((label, day) => {
                  const on = schedule.weekdays.includes(day)
                  return (
                    <button
                      key={label}
                      type="button"
                      className={on ? 'btn btn--sm btn--primary' : 'btn btn--sm'}
                      aria-pressed={on}
                      onClick={() => setSchedule({ weekdays: on ? schedule.weekdays.filter((d) => d !== day) : [...schedule.weekdays, day].sort() })}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>
            </Field>
          )}

          <div className="wa-row">
            <Field label={schedule.type === 'once' ? 'Run at' : 'Start (optional)'}>
              <input className="input" type="datetime-local" value={toLocalInput(schedule.startAt)} onChange={(e) => setSchedule({ startAt: fromLocalInput(e.target.value) })} />
            </Field>
            {schedule.type !== 'once' && (
              <Field label="End (optional)">
                <input className="input" type="datetime-local" value={toLocalInput(schedule.endAt)} onChange={(e) => setSchedule({ endAt: fromLocalInput(e.target.value) })} />
              </Field>
            )}
            <Field label="Max notifications per run" hint="Merchants past the cap are picked up next run.">
              <input className="input" type="number" min={1} max={10000} value={schedule.maxPerRun} onChange={(e) => setSchedule({ maxPerRun: Math.max(1, Number(e.target.value) || 1) })} />
            </Field>
          </div>

          <div className="wa-row">
            <label className="checkbox-row">
              <input type="checkbox" checked={schedule.quietHours.enabled} onChange={(e) => setSchedule({ quietHours: { ...schedule.quietHours, enabled: e.target.checked } })} />
              <span>Quiet hours — hold runs until they end</span>
            </label>
            <label className="checkbox-row">
              <input type="checkbox" checked={schedule.businessDaysOnly} onChange={(e) => setSchedule({ businessDaysOnly: e.target.checked })} />
              <span>Business days only (Mon–Fri)</span>
            </label>
          </div>
          {schedule.quietHours.enabled && (
            <div className="wa-row">
              <Field label="Quiet from">
                <input className="input" type="time" value={schedule.quietHours.start} onChange={(e) => setSchedule({ quietHours: { ...schedule.quietHours, start: e.target.value } })} />
              </Field>
              <Field label="Quiet until">
                <input className="input" type="time" value={schedule.quietHours.end} onChange={(e) => setSchedule({ quietHours: { ...schedule.quietHours, end: e.target.value } })} />
              </Field>
            </div>
          )}
        </div>
      </Card>

      <Card title="Frequency controls" subtitle="So nobody is messaged every five minutes.">
        <div className="wa-form">
          <Segmented
            label="Repeat policy"
            value={frequency.repeat}
            onChange={(repeat) => setFrequency({ repeat })}
            options={[
              { key: 'once_per_crossing', label: 'Once per threshold crossing' },
              { key: 'repeat', label: 'Remind while it stays true' },
            ]}
          />
          <div className="wa-row">
            {frequency.repeat === 'repeat' && (
              <Field label="Minimum interval between reminders (hours)">
                <input className="input" type="number" min={0.25} step={0.25} value={frequency.minIntervalHours} onChange={(e) => setFrequency({ minIntervalHours: Math.max(0.25, Number(e.target.value) || 24) })} />
              </Field>
            )}
            <Field label="Cooldown after any alert (hours)" hint="0 = none.">
              <input className="input" type="number" min={0} step={0.5} value={frequency.cooldownHours} onChange={(e) => setFrequency({ cooldownHours: Math.max(0, Number(e.target.value) || 0) })} />
            </Field>
            <Field label="Max alerts per merchant per day">
              <input className="input" type="number" min={1} max={24} value={frequency.maxPerDay} onChange={(e) => setFrequency({ maxPerDay: Math.min(24, Math.max(1, Number(e.target.value) || 1)) })} />
            </Field>
          </div>
          <span className="field__hint">
            Reset: the alert re-arms when the condition clears{form.triggerConfig.recoveryAmount !== null ? ` and the balance reaches the recovery threshold (${money(form.triggerConfig.recoveryAmount, form.currency ?? 'INR')})` : ''}.
          </span>
        </div>
      </Card>

      <div className="wa-summary">
        <strong>Summary: </strong>
        {ruleSummary(form)}
      </div>
    </>
  )
}

// ── Step 7 ──────────────────────────────────────────────────────────────────

interface SimulateResult {
  merchant: { id: string; name: string; balance: string; currency: string; status: string }
  inAudience: boolean
  conditionMet: boolean
  wouldSend: boolean
  reason: string
  messages: Array<{ channel: string; fallback: boolean; recipient: string | null; recipientMissing: boolean; template: string | null; subject: string | null; body: string | null; unknownVariables: string[] }>
  issues: Issue[]
}

const REASONS: Record<string, string> = {
  crossed: 'Would alert now (first crossing).',
  reminder: 'Would send a reminder now.',
  not_met: 'Condition not met — no alert.',
  not_in_audience: 'This merchant is not in the audience.',
  cooldown: 'Held by the cooldown.',
  min_interval: 'Held by the minimum interval.',
  daily_cap: 'Held by the daily cap.',
  already_alerted: 'Already alerted for this crossing.',
}

function ReviewStep({ form, onActivate, onDraft, saving }: { form: RuleForm; onActivate: () => void; onDraft: () => void; saving: boolean }): React.ReactNode {
  const payload = useMemo(() => JSON.stringify(form), [form])
  const validation = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'validate', payload],
    queryFn: () => api<{ issues: Issue[]; nextRunAt: string | null }>('/v1/admin/wallet-alerts/rules/validate', { method: 'POST', body: form }),
    retry: false,
  })

  const errors = validation.data?.issues.filter((i) => i.level === 'error') ?? []
  const warnings = validation.data?.issues.filter((i) => i.level === 'warning') ?? []

  return (
    <>
      <Card title="Review" subtitle="What this rule will do, exactly as configured.">
        <div className="wa-form">
          <div className="wa-summary">{ruleSummary(form)}</div>
          {validation.error && <Notice kind="error" title="This configuration is invalid">{errorMessage(validation.error)}</Notice>}
          {validation.data && errors.length === 0 && (
            <Notice kind="ok" title="Ready to activate">
              {validation.data.nextRunAt ? `First run: ${dateTime(validation.data.nextRunAt)}.` : ''}
            </Notice>
          )}
          {errors.length > 0 && (
            <Notice kind="error" title="Fix these before activating">
              <ul style={{ margin: '4px 0 0 18px' }}>
                {errors.map((issue) => (
                  <li key={issue.message}>{issue.message}</li>
                ))}
              </ul>
            </Notice>
          )}
          {warnings.length > 0 && (
            <Notice kind="warn" title="Worth a look">
              <ul style={{ margin: '4px 0 0 18px' }}>
                {warnings.map((issue) => (
                  <li key={issue.message}>{issue.message}</li>
                ))}
              </ul>
            </Notice>
          )}
        </div>
      </Card>

      <TestPanel form={form} />

      <Card title="Go live">
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn" disabled={saving || form.name.trim().length < 2} onClick={onDraft}>
            Save as draft
          </button>
          <button type="button" className="btn btn--primary" disabled={saving || errors.length > 0 || !validation.data} onClick={onActivate}>
            <CheckCircleIcon size={16} /> Activate rule…
          </button>
        </div>
      </Card>
    </>
  )
}

function TestPanel({ form }: { form: RuleForm }): React.ReactNode {
  const [q, setQ] = useState('')
  const search = useDebounced(q, 300)
  const [merchant, setMerchant] = useState<{ id: string; name: string } | null>(null)
  const [recipient, setRecipient] = useState({ email: '', whatsapp: '' })

  const results = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'merchants', search],
    queryFn: () => api<{ items: Array<{ id: string; name: string; balance: string; currency: string }> }>('/v1/admin/wallet-alerts/merchants', { query: { q: search } }),
    enabled: search.trim().length > 0 && !merchant,
  })

  const simulate = useMutation({
    mutationFn: () => api<SimulateResult>('/v1/admin/wallet-alerts/simulate', { method: 'POST', body: { rule: form, merchantId: merchant!.id } }),
    onError: (error) => toast('error', 'Simulation failed', errorMessage(error)),
  })

  const send = useMutation({
    mutationFn: (channel: 'email' | 'whatsapp') =>
      api('/v1/admin/wallet-alerts/test-send', {
        method: 'POST',
        body: {
          channel,
          templateId: channel === 'email' ? form.emailTemplateId : form.whatsappTemplateId,
          merchantId: merchant?.id,
          recipient: recipient[channel],
          ruleName: form.name || 'Test alert',
          thresholdAmount: form.thresholdAmount,
        },
      }),
    onSuccess: (_, channel) => toast('ok', `Test ${channel === 'email' ? 'email' : 'WhatsApp'} sent`, `To ${recipient[channel]} only.`),
    onError: (error) => toast('error', 'Test send failed', errorMessage(error)),
  })

  const routing = form.channels.routing

  return (
    <Card title="Test & preview" subtitle="Simulation never sends a message or touches a wallet.">
      <div className="wa-form">
        <Field label="Simulate against a merchant">
          {merchant ? (
            <div className="row">
              <span className="wa-chip">
                {merchant.name} #{merchant.id}
                <button type="button" aria-label="Clear" onClick={() => { setMerchant(null); simulate.reset() }}>
                  <XIcon size={12} />
                </button>
              </span>
              <button type="button" className="btn btn--sm btn--primary" disabled={simulate.isPending} onClick={() => simulate.mutate()}>
                {simulate.isPending ? 'Simulating…' : 'Run simulation'}
              </button>
            </div>
          ) : (
            <input className="input" type="search" placeholder="Search merchant by name, email, phone or ID" value={q} onChange={(e) => setQ(e.target.value)} />
          )}
        </Field>
        {!merchant && results.data && results.data.items.length > 0 && (
          <div className="wa-chips">
            {results.data.items.slice(0, 8).map((m) => (
              <button key={m.id} type="button" className="btn btn--sm" onClick={() => setMerchant({ id: m.id, name: m.name })}>
                {m.name} · {money(m.balance, m.currency)}
              </button>
            ))}
          </div>
        )}

        {simulate.data && (
          <>
            <Notice kind={simulate.data.wouldSend ? 'ok' : 'info'} title={REASONS[simulate.data.reason] ?? simulate.data.reason}>
              Balance {money(simulate.data.merchant.balance, simulate.data.merchant.currency)} · in audience: {simulate.data.inAudience ? 'yes' : 'no'} · condition met:{' '}
              {simulate.data.conditionMet ? 'yes' : 'no'}
            </Notice>
            {simulate.data.messages.map((message) => (
              <div key={message.channel} className="wa-group">
                <div className="wa-group__head">
                  <span>
                    {message.channel === 'email' ? 'Email' : 'WhatsApp'}
                    {message.fallback ? ' (fallback)' : ''} → {message.recipientMissing ? 'no recipient on file' : message.recipient}
                  </span>
                  <span>{message.template ?? 'no template'}</span>
                </div>
                {message.unknownVariables.length > 0 && <Notice kind="error" title={`Unknown variables: ${message.unknownVariables.join(', ')}`} />}
                {message.subject && <strong style={{ fontSize: '.85rem' }}>Subject: {message.subject}</strong>}
                {message.body &&
                  (message.channel === 'email' ? (
                    <iframe title="Rendered email" className="wa-email-frame" sandbox="" style={{ minHeight: 240 }} srcDoc={`<!doctype html><meta charset="utf-8"><body style="font-family:sans-serif;padding:16px">${message.body}</body>`} />
                  ) : (
                    <div className="wa-bubble-wrap">
                      <div className="wa-bubble">{message.body}</div>
                    </div>
                  ))}
              </div>
            ))}
          </>
        )}

        <hr style={{ border: 0, borderTop: '1px solid var(--border)', width: '100%' }} />
        <Notice kind="warn" title="Live test delivery">
          Sends one real message to the address you type below — never to the merchant. Rendered with {merchant ? `${merchant.name}'s` : 'sample'} data. Limited to 10 per hour.
        </Notice>
        <div className="wa-row">
          {routing !== 'whatsapp' && (
            <Field label="Test email recipient">
              <div className="row" style={{ gap: 6 }}>
                <input className="input" type="email" placeholder="you@company.com" value={recipient.email} onChange={(e) => setRecipient({ ...recipient, email: e.target.value })} />
                <button type="button" className="btn" disabled={!form.emailTemplateId || !recipient.email || send.isPending} onClick={() => send.mutate('email')}>
                  Send test email
                </button>
              </div>
            </Field>
          )}
          {routing !== 'email' && (
            <Field label="Test WhatsApp number">
              <div className="row" style={{ gap: 6 }}>
                <input className="input" type="tel" placeholder="98765 43210" value={recipient.whatsapp} onChange={(e) => setRecipient({ ...recipient, whatsapp: e.target.value })} />
                <button type="button" className="btn" disabled={!form.whatsappTemplateId || !recipient.whatsapp || send.isPending} onClick={() => send.mutate('whatsapp')}>
                  Send test WhatsApp
                </button>
              </div>
            </Field>
          )}
        </div>
      </div>
    </Card>
  )
}
